import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, Search, Columns3, Download, ChevronDown, Check } from 'lucide-react';
import { apiCall, retryBanfSapTransfer, getUserPreference, setUserPreference, downloadBanfExport } from 'core/api/apiService';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { useToast } from '../../core/shell/ToastProvider';
import ConfirmDialog from '../../core/ui/ConfirmDialog';
import MultiSelectDropdown from '../../core/ui/MultiSelectDropdown';
import MoreFiltersPicker from '../../core/ui/MoreFiltersPicker';
import useVisibleFilters from '../../core/ui/useVisibleFilters';
import usePersistedFilters from '../../core/ui/usePersistedFilters';
import { deriveBanfFilterOptions, applyBanfFilters } from 'core/utils/banfFilters';
import BanfKpiRow from './BanfKpiRow';
import BanfTable from './BanfTable';
import BanfDetailDrawer from './BanfDetailDrawer';
import { computeKpis, matchesQuery, fmtAmount, OPTIONAL_COLUMNS, DEFAULT_OPTIONAL_COLUMNS } from './banfListUtils';

const FILTER_ALL_KEYS = ['anforderer', 'kostl', 'abteilung', 'ekgrp', 'matkl', 'werk', 'material', 'lieferant', 'belegart', 'kontierung'];
const FILTER_DEFAULT_KEYS = ['anforderer', 'abteilung', 'matkl'];
const EMPTY_FILTERS = Object.fromEntries(FILTER_ALL_KEYS.map((k) => [k, []]));

/**
 * Neue BANF-Listenansicht (Design-Handoff 28.07.): volle Breite, KPI-Zeile, Status-Tabs,
 * Tabelle mit konfigurierbaren Spalten + Dichte, Sammelfreigabe, Detail-Drawer.
 * Ersetzt PurchaseRequestsView; nutzt dieselben APIs/Props.
 */
const BanfListView = ({ user, onBack, onRequestUpdate, onDraftsChange, onEditDraft, initialTab, navNonce }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const { showSuccess, showError, showWarning } = useToast();

  const [requests, setRequests] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [tab, setTab] = useState(initialTab || 'all');
  const [query, setQuery] = useState('');
  const [selection, setSelection] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [density, setDensity] = useState('comfortable');
  const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null); // { done, total } waehrend Sammelaktion

  const [fieldFilters, setFieldFilters] = usePersistedFilters(user, 'banf-filter-values', EMPTY_FILTERS);
  const [visibleFilterKeys, setVisibleFilterKeys] = useVisibleFilters(user, 'banf-filter-fields', FILTER_DEFAULT_KEYS, FILTER_ALL_KEYS);
  const [visibleColumns, setVisibleColumns] = useVisibleFilters(user, 'banf-list-columns', DEFAULT_OPTIONAL_COLUMNS, OPTIONAL_COLUMNS);

  const canDecide = user.role === 'admin' || user.role === 'supervisor';
  const canDelete = user.role === 'admin';

  // Auswahl darf Filter-/Suchwechsel nicht ueberleben (Reviewer: sonst genehmigt die
  // Sammelleiste unsichtbare BANFs).
  useEffect(() => { setSelection([]); }, [fieldFilters, query]);

  // Tab-Vorwahl auch nach dem Mount uebernehmen (z. B. Absprung aus der
  // Benachrichtigungs-Glocke, waehrend die Liste bereits offen ist). navNonce zaehlt
  // je Absprung hoch, damit auch der WIEDERHOLTE Sprung auf denselben Tab wirkt.
  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab, navNonce]);

  const load = async () => {
    try {
      setLoading(true);
      const [reqData, draftData] = await Promise.all([
        apiCall('/purchase-requests', 'GET', null, user),
        apiCall('/purchase-requests/drafts', 'GET', null, user).catch(() => []),
      ]);
      setRequests(Array.isArray(reqData) ? reqData : []);
      setDrafts(Array.isArray(draftData) ? draftData : []);
      setLoadError(false);
    } catch (e) {
      console.error('Failed to load purchase requests:', e);
      setLoadError(true);
      showError(t('pr.loadError'));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Dichte pro User persistieren (userChanged-Flag: Klick vor GET-Aufloesung gewinnt)
  const densityTouched = React.useRef(false);
  useEffect(() => {
    let alive = true;
    getUserPreference(user, 'banf-list-density')
      .then((p) => { if (alive && !densityTouched.current && (p?.value === 'compact' || p?.value === 'comfortable')) setDensity(p.value); })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const changeDensity = (d) => { densityTouched.current = true; setDensity(d); setUserPreference(user, 'banf-list-density', d).catch(() => {}); };

  // ---- Ableitungen (alles live berechnet, Design-Vorgabe) --------------------------------------
  const kpis = useMemo(() => computeKpis(requests), [requests]);
  const filterOptions = useMemo(() => deriveBanfFilterOptions(requests), [requests]);

  const byTab = (r) => {
    if (tab === 'deleted') return Boolean(r.deleted_at);
    if (r.deleted_at) return false; // Geloeschte nur im eigenen Tab
    if (tab === 'all') return true;
    if (tab === 'transferred') return r.sap_transfer_status === 'posted';
    if (tab === 'sap_failed') return r.sap_transfer_status === 'failed';
    return r.status === tab;
  };
  const filtered = useMemo(
    () => applyBanfFilters(requests, fieldFilters).filter(byTab).filter((r) => matchesQuery(r, query)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [requests, fieldFilters, tab, query]
  );
  const rows = useMemo(() => {
    if (tab === 'drafts') {
      return drafts
        .filter((d) => !query.trim() || (d.title || '').toLowerCase().includes(query.trim().toLowerCase()))
        .map((d) => ({ isDraft: true, id: d.id, title: d.title, total_amount: d.total_amount, currency: d.currency, created_at: d.created_at, raw: d }));
    }
    return filtered;
  }, [tab, drafts, filtered, query]);

  const counts = useMemo(() => {
    // Alle Zaehler auf derselben Basis (Feldfilter + Suche) — Handoff-Vorgabe "stets live,
    // niemals inkonsistent".
    const base = applyBanfFilters(requests, fieldFilters).filter((r) => matchesQuery(r, query));
    const active = base.filter((r) => !r.deleted_at);
    return {
      all: active.length,
      pending: active.filter((r) => r.status === 'pending').length,
      approved: active.filter((r) => r.status === 'approved').length,
      transferred: active.filter((r) => r.sap_transfer_status === 'posted').length,
      sapFailed: active.filter((r) => r.sap_transfer_status === 'failed').length,
      rejected: active.filter((r) => r.status === 'rejected').length,
      deleted: base.filter((r) => Boolean(r.deleted_at)).length,
      drafts: drafts.filter((d) => !query.trim() || (d.title || '').toLowerCase().includes(query.trim().toLowerCase())).length,
    };
  }, [requests, drafts, fieldFilters, query]);

  const visiblePending = rows.filter((r) => !r.isDraft && !r.deleted_at && r.status === 'pending');
  const selectedRequests = requests.filter((r) => selection.includes(r.id));
  const selectedPending = selectedRequests.filter((r) => r.status === 'pending');
  const selectedSum = selectedRequests.reduce((s, r) => s + Number(r.total_amount || 0), 0);

  // ---- Aktionen --------------------------------------------------------------------------------
  const refresh = async () => { await load(); if (onRequestUpdate) onRequestUpdate(); };

  const decide = async (id, status, comment = '') => {
    const res = await apiCall(`/purchase-requests/${id}/status`, 'PUT', { status, comment }, user);
    return res;
  };

  const askApprove = (id) => setConfirmDialog({
    message: t('pr.approveConfirm'), confirmLabel: t('pr.approve'), variant: 'success',
    onConfirm: async () => {
      try {
        setProcessing(true);
        const res = await decide(id, 'approved');
        if (res?.sap?.banfn) showSuccess(`BANF #${id} ${t('bl.approvedToast')} — SAP: ${res.sap.banfn}`);
        else if (res?.sap?.transferStatus === 'failed') showWarning(`BANF #${id} ${t('bl.approvedToast')} — ${t('pr.sapTransferFailedShort')}`);
        else showSuccess(`BANF #${id} ${t('bl.approvedToast')}`);
        setOpenId(null);
        await refresh();
      } catch (e) { showError(t('pr.processError', { error: e.message })); }
      finally { setProcessing(false); setConfirmDialog(null); }
    },
  });

  const askReject = (id) => setConfirmDialog({
    message: t('pr.rejectConfirm'), confirmLabel: t('pr.reject'), variant: 'danger',
    inputPlaceholder: t('pr.commentPlaceholder'),
    onConfirm: async (comment) => {
      try {
        setProcessing(true);
        await decide(id, 'rejected', (comment || '').trim());
        showSuccess(`BANF #${id} ${t('bl.rejectedToast')}`);
        setOpenId(null);
        await refresh();
      } catch (e) { showError(t('pr.processError', { error: e.message })); }
      finally { setProcessing(false); setConfirmDialog(null); }
    },
  });

  // Sammelfreigabe (Design §5): wirkt nur auf ausstehende; übersprungene werden benannt.
  const bulkDecide = (status) => {
    if (selectedPending.length === 0) { showWarning(t('bl.bulkNonePending')); return; }
    // Jede Freigabe loest synchron eine SAP-Buchung aus — Obergrenze gegen Minuten-Schleifen.
    if (selectedPending.length > 25) { showWarning(t('bl.bulkTooMany')); return; }
    const skipped = selection.length - selectedPending.length;
    setConfirmDialog({
      message: t(status === 'approved' ? 'bl.bulkApproveConfirm' : 'bl.bulkRejectConfirm', { n: selectedPending.length }),
      confirmLabel: status === 'approved' ? t('pr.approve') : t('pr.reject'),
      variant: status === 'approved' ? 'success' : 'danger',
      onConfirm: async () => {
        setProcessing(true);
        let ok = 0; let failed = 0; const banfns = [];
        let done = 0;
        for (const r of selectedPending) {
          setBulkProgress({ done, total: selectedPending.length });
          try {
            const res = await decide(r.id, status);
            ok += 1;
            if (res?.sap?.banfn) banfns.push(res.sap.banfn);
          } catch { failed += 1; }
          done += 1;
        }
        setBulkProgress(null);
        setProcessing(false); setConfirmDialog(null); setSelection([]);
        const parts = [`${ok} ${status === 'approved' ? t('bl.bulkApproved') : t('bl.bulkRejected')}`];
        if (banfns.length) parts.push(`SAP: ${banfns.join(', ')}`);
        if (skipped > 0) parts.push(`${skipped} ${t('bl.bulkSkipped')}`);
        if (failed > 0) parts.push(`${failed} ${t('bl.bulkFailed')}`);
        (failed > 0 ? showWarning : showSuccess)(parts.join(' · '));
        await refresh();
      },
    });
  };

  const handleReorder = (request) => setConfirmDialog({
    message: t('pr.reorderConfirm', { id: request.id }), confirmLabel: t('pr.reorder'), variant: 'brand',
    onConfirm: () => {
      const parse = (v) => (Array.isArray(v) ? v : (typeof v === 'string' && v.trim().startsWith('[') ? JSON.parse(v) : []));
      const items = (request.items || []).map((it) => ({
        ...it,
        cost_center: it.cost_center || it.kostenstelle || '',
        gl_account: it.gl_account || it.sachkonto || '',
        material: it.material || it.matnr || '',
        unit: it.unit || 'ST',
        price_unit: parseInt(it.price_unit, 10) || 1,
        kontierungstyp: it.kontierungstyp || 'kostenstelle',
        services: (() => { try { return parse(it.services); } catch { return []; } })(),
        attachments: (() => { try { return parse(it.attachments); } catch { return []; } })(),
        delivery_date: '',
      }));
      setConfirmDialog(null); setOpenId(null);
      if (onEditDraft) onEditDraft({ id: null, items, belegart: request.items?.[0]?.belegart || 'NB' });
    },
  });

  const handleDelete = (request) => setConfirmDialog({
    message: t('pr.deleteConfirm', { id: request.id, name: request.requester_name }),
    confirmLabel: t('pr.deleteRequest'), variant: 'danger',
    onConfirm: async () => {
      try {
        setProcessing(true);
        await apiCall(`/purchase-requests/${request.id}`, 'DELETE', null, user);
        showSuccess(t('pr.requestDeleted', { id: request.id }));
        setOpenId(null);
        await refresh();
      } catch (e) { showError(t('pr.deleteError', { error: e.message })); }
      finally { setProcessing(false); setConfirmDialog(null); }
    },
  });

  const handleDeleteDraft = (draftId) => setConfirmDialog({
    message: t('pr.draftDeleteConfirm'), confirmLabel: t('pr.deleteDraft'), variant: 'danger',
    onConfirm: async () => {
      try {
        setProcessing(true);
        await apiCall(`/purchase-requests/drafts/${draftId}`, 'DELETE', null, user);
        showSuccess(t('pr.draftDeleted'));
        await load();
        if (onDraftsChange) onDraftsChange();
      } catch { showError(t('pr.draftDeleteError')); }
      finally { setProcessing(false); setConfirmDialog(null); }
    },
  });

  const handleRetrySap = async (id) => {
    try {
      setProcessing(true);
      await retryBanfSapTransfer(user, id);
      showSuccess(t('bl.retryStarted'));
      await refresh();
    } catch (e) { showError(e.message); }
    finally { setProcessing(false); }
  };

  const handleExport = async () => {
    try {
      // Zentraler API-Layer (Dateiname, x-row-count-Leerbehandlung) + aktive Filter mitgeben.
      await downloadBanfExport(user, {
        status: tab !== 'all' && tab !== 'drafts' && tab !== 'transferred' ? tab : '',
        kostl: fieldFilters.kostl, ekgrp: fieldFilters.ekgrp, matkl: fieldFilters.matkl,
      });
    } catch (e) { showError(t('bl.exportError')); }
  };

  const resetFilters = () => { setFieldFilters(EMPTY_FILTERS); setQuery(''); };
  const toggleSelect = (id) => setSelection((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleSelectAll = () => {
    const ids = rows.filter((r) => !r.isDraft).map((r) => r.id);
    setSelection((s) => (ids.every((id) => s.includes(id)) ? s.filter((id) => !ids.includes(id)) : [...new Set([...s, ...ids])]));
  };

  // ---- Render ---------------------------------------------------------------------------------
  const pill = (active) => `px-3.5 py-1.5 rounded-full text-[13px] font-medium transition-colors ${
    active
      ? (isDarkMode ? 'bg-blue-500/20 text-blue-200' : 'bg-brand/10 text-brand')
      : (isDarkMode ? 'text-white/60 hover:text-white hover:bg-white/5' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100')
  }`;

  const TABS = [
    ['all', t('bl.tabAll'), counts.all], ['pending', t('bl.stPending'), counts.pending],
    ['approved', t('bl.stApproved'), counts.approved], ['transferred', t('bl.stTransferred'), counts.transferred],
    ['sap_failed', t('bl.tabSapFailed'), counts.sapFailed],
    ['rejected', t('bl.stRejected'), counts.rejected], ['deleted', t('bl.stDeleted'), counts.deleted], ['drafts', t('bl.tabDrafts'), counts.drafts],
  ];

  const hasActiveFilters = Object.values(fieldFilters).some((a) => Array.isArray(a) && a.length > 0) || query.trim();
  const setFF = (k) => (next) => setFieldFilters((f) => ({ ...f, [k]: next }));
  const bulkProps = { selectAllLabel: t('kpi.selectAll'), clearAllLabel: t('kpi.clearAll'), searchPlaceholder: t('kpi.search') };
  const filterDefs = [
    { key: 'anforderer', label: t('kpi.filterAnforderer'), node: <MultiSelectDropdown label={t('kpi.filterAnforderer')} options={filterOptions.anforderer} selected={fieldFilters.anforderer} onChange={setFF('anforderer')} searchable width="w-56" {...bulkProps} /> },
    { key: 'kostl', label: t('kpi.filterKostl'), node: <MultiSelectDropdown label={t('kpi.filterKostl')} options={filterOptions.kostl} selected={fieldFilters.kostl} onChange={setFF('kostl')} searchable width="w-48" {...bulkProps} /> },
    { key: 'abteilung', label: t('kpi.filterAbteilung'), node: <MultiSelectDropdown label={t('kpi.filterAbteilung')} options={filterOptions.abteilung} selected={fieldFilters.abteilung} onChange={setFF('abteilung')} searchable width="w-48" {...bulkProps} /> },
    { key: 'ekgrp', label: t('kpi.filterEkgrp'), node: <MultiSelectDropdown label={t('kpi.filterEkgrp')} options={filterOptions.ekgrp} selected={fieldFilters.ekgrp} onChange={setFF('ekgrp')} searchable width="w-56" {...bulkProps} /> },
    { key: 'matkl', label: t('kpi.filterMatkl'), node: <MultiSelectDropdown label={t('kpi.filterMatkl')} options={filterOptions.matkl} selected={fieldFilters.matkl} onChange={setFF('matkl')} searchable width="w-56" {...bulkProps} /> },
    { key: 'werk', label: t('kpi.filterWerk'), node: <MultiSelectDropdown label={t('kpi.filterWerk')} options={filterOptions.werk} selected={fieldFilters.werk} onChange={setFF('werk')} searchable width="w-40" {...bulkProps} /> },
    { key: 'material', label: t('kpi.filterMaterial'), node: <MultiSelectDropdown label={t('kpi.filterMaterial')} options={filterOptions.material} selected={fieldFilters.material} onChange={setFF('material')} searchable width="w-48" {...bulkProps} /> },
    { key: 'lieferant', label: t('kpi.filterLieferant'), node: <MultiSelectDropdown label={t('kpi.filterLieferant')} options={filterOptions.lieferant} selected={fieldFilters.lieferant} onChange={setFF('lieferant')} searchable width="w-56" {...bulkProps} /> },
    { key: 'belegart', label: t('kpi.filterBelegart'), node: <MultiSelectDropdown label={t('kpi.filterBelegart')} options={filterOptions.belegart} selected={fieldFilters.belegart} onChange={setFF('belegart')} searchable width="w-40" {...bulkProps} /> },
    { key: 'kontierung', label: t('kpi.filterKontierung'), node: <MultiSelectDropdown label={t('kpi.filterKontierung')} options={filterOptions.kontierung} selected={fieldFilters.kontierung} onChange={setFF('kontierung')} searchable width="w-48" {...bulkProps} /> },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className={`w-8 h-8 border-3 rounded-full animate-spin ${isDarkMode ? 'border-white/20 border-t-white' : 'border-slate-200 border-t-brand'}`} />
      </div>
    );
  }

  const openRequest = openId ? requests.find((r) => r.id === openId) : null;

  if (loadError && requests.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <p className={`text-sm font-medium ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>{t('bl.loadFailed')}</p>
        <button onClick={load} className={`px-4 py-2 rounded-full text-sm font-medium ${isDarkMode ? 'bg-blue-600/70 text-white hover:bg-blue-600' : 'bg-brand text-white hover:bg-[#1D4ED8]'}`}>
          {t('bl.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3.5 h-full w-full max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-8 py-5">
      {/* Kopf */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className={`p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-white/10 text-white/70' : 'hover:bg-slate-100 text-slate-500'}`}>
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className={`text-[21px] font-extrabold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('bl.title')}</h1>
            <p className={`text-[12.5px] ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{t('bl.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Dichte */}
          <div className={`flex rounded-full border p-0.5 ${isDarkMode ? 'border-white/15 bg-gray-900/40' : 'border-slate-200 bg-white'}`}>
            <button onClick={() => changeDensity('comfortable')} className={pill(density === 'comfortable')}>{t('bl.comfortable')}</button>
            <button onClick={() => changeDensity('compact')} className={pill(density === 'compact')}>{t('bl.compact')}</button>
          </div>
          {/* Spalten */}
          <div className="relative">
            <button onClick={() => setColumnsMenuOpen((o) => !o)}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border text-[13px] font-medium transition-colors ${isDarkMode ? 'border-white/15 text-white/80 hover:bg-white/10' : 'border-slate-200 text-slate-600 hover:bg-slate-50 bg-white'}`}>
              <Columns3 className="w-4 h-4" /> {t('bl.columns')} ({5 + visibleColumns.length}) <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {columnsMenuOpen && (
              <>
                <div className="fixed inset-0 z-[55]" onClick={() => setColumnsMenuOpen(false)} />
                <div className={`absolute right-0 top-full mt-1 z-[56] w-56 rounded-3xl border shadow-xl p-1.5 ${isDarkMode ? 'bg-gray-900/95 border-white/15 backdrop-blur-xl' : 'bg-white border-slate-200'}`}>
                  {OPTIONAL_COLUMNS.map((k) => {
                    const on = visibleColumns.includes(k);
                    return (
                      <button key={k} type="button"
                        onClick={() => setVisibleColumns(on ? visibleColumns.filter((x) => x !== k) : [...visibleColumns, k])}
                        className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-sm rounded-full transition-colors text-left ${isDarkMode ? 'text-white/85 hover:bg-white/10' : 'text-slate-700 hover:bg-slate-50'}`}>
                        <span className={`w-[17px] h-[17px] rounded-full border-2 flex items-center justify-center ${on ? 'bg-brand border-brand text-white' : (isDarkMode ? 'border-white/25' : 'border-slate-300')}`}>
                          {on && <Check className="w-3 h-3" strokeWidth={3} />}
                        </span>
                        {t(`bl.col${k.charAt(0).toUpperCase()}${k.slice(1)}`)}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          <button onClick={handleExport}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border text-[13px] font-medium transition-colors ${isDarkMode ? 'border-white/15 text-white/80 hover:bg-white/10' : 'border-slate-200 text-slate-600 hover:bg-slate-50 bg-white'}`}>
            <Download className="w-4 h-4" /> {t('bl.export')}
          </button>
        </div>
      </div>

      {/* KPI-Zeile */}
      <BanfKpiRow
        kpis={kpis}
        onGoPending={() => setTab('pending')}
        onGoSap={() => setTab(kpis.failedCount > 0 ? 'sap_failed' : 'transferred')}
      />

      {/* Tabs + Schnellauswahl + Suche */}
      <div className="flex flex-wrap items-center gap-2">
        <div className={`flex flex-wrap rounded-full border p-0.5 ${isDarkMode ? 'border-white/15 bg-gray-900/40' : 'border-slate-200 bg-white'}`}>
          {TABS.map(([key, label, count]) => (
            <button key={key} onClick={() => { setTab(key); setSelection([]); }} className={pill(tab === key)}>
              {label}{' '}
              <span className={`font-mono ${key === 'sap_failed' && count > 0 && tab !== key ? (isDarkMode ? 'text-red-300 font-semibold' : 'text-red-600 font-semibold') : ''}`}>
                {count}
              </span>
            </button>
          ))}
        </div>
        {visiblePending.length >= 2 && !visiblePending.every((r) => selection.includes(r.id)) && canDecide && (
          <button onClick={() => setSelection([...new Set([...selection, ...visiblePending.map((r) => r.id)])])}
            className={`px-3.5 py-1.5 rounded-full text-[13px] font-medium border transition-colors ${isDarkMode ? 'border-amber-400/40 text-amber-300 hover:bg-amber-500/10' : 'border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100'}`}>
            ✓ {t('bl.selectAllPending', { n: visiblePending.length })}
          </button>
        )}
        <div className="relative flex-1 min-w-[220px] max-w-[420px] ml-auto">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('bl.searchPlaceholder')}
            className={`w-full pl-9 pr-3 py-2 rounded-full border text-[13px] focus:outline-none focus:ring-2 transition-all ${
              isDarkMode ? 'bg-gray-900/40 border-white/15 text-white placeholder-white/40 focus:ring-blue-400/40' : 'bg-white border-slate-200 text-slate-800 placeholder-slate-400 focus:ring-brand/30'
            }`}
          />
        </div>
      </div>

      {/* Filterleiste */}
      {tab !== 'drafts' && (
        <div className="flex flex-wrap items-center gap-2">
          {filterDefs.filter((d) => visibleFilterKeys.includes(d.key)).map((d) => <React.Fragment key={d.key}>{d.node}</React.Fragment>)}
          <MoreFiltersPicker
            filters={filterDefs.map(({ key, label }) => ({ key, label }))}
            visibleKeys={visibleFilterKeys}
            onChange={(next) => {
              const removed = visibleFilterKeys.filter((k) => !next.includes(k));
              if (removed.length) setFieldFilters((f) => ({ ...f, ...Object.fromEntries(removed.map((k) => [k, []])) }));
              setVisibleFilterKeys(next);
            }}
            addLabel={t('kpi.moreFilters')}
            title={t('products.filterFields')}
          />
          {hasActiveFilters && (
            <button onClick={resetFilters} className={`ml-auto text-[12.5px] underline ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
              {t('bl.resetFilters')}
            </button>
          )}
        </div>
      )}

      {/* Tabelle */}
      <BanfTable
        rows={rows}
        visibleColumns={visibleColumns}
        density={density}
        selection={selection}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={toggleSelectAll}
        canDecide={canDecide}
        canDelete={canDelete}
        processing={processing}
        openId={openId}
        onOpen={setOpenId}
        onApprove={askApprove}
        onReject={askReject}
        onReorder={handleReorder}
        onDelete={handleDelete}
        onEditDraft={onEditDraft}
        onDeleteDraft={handleDeleteDraft}
        onResetFilters={resetFilters}
      />

      {/* Sammelfreigabe-Leiste */}
      {selection.length > 0 && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[58]">
          <div className={`flex items-center gap-3 px-4 py-2.5 rounded-full shadow-2xl ${isDarkMode ? 'bg-white text-slate-900' : 'bg-slate-900 text-white'}`}>
            <span className="text-[13px] font-medium">
              {bulkProgress
                ? <span className="font-mono">{bulkProgress.done}/{bulkProgress.total}…</span>
                : <>{selection.length} {t('bl.selected')} · <span className="font-mono">{fmtAmount(selectedSum)}</span></>}
            </span>
            {canDecide && (
              <>
                <button onClick={() => bulkDecide('approved')} disabled={processing}
                  className="px-3.5 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[13px] font-semibold transition-colors disabled:opacity-50">
                  ✓ {selectedPending.length} {t('bl.bulkApproveBtn')}
                </button>
                <button onClick={() => bulkDecide('rejected')} disabled={processing}
                  className={`px-3 py-1.5 rounded-full text-[13px] font-medium transition-colors disabled:opacity-50 ${isDarkMode ? 'text-slate-600 hover:bg-slate-100' : 'text-white/70 hover:bg-white/10'}`}>
                  {t('pr.reject')}
                </button>
              </>
            )}
            <button onClick={() => setSelection([])}
              className={`px-3 py-1.5 rounded-full text-[13px] font-medium transition-colors ${isDarkMode ? 'text-slate-600 hover:bg-slate-100' : 'text-white/70 hover:bg-white/10'}`}>
              {t('bl.clearSelection')}
            </button>
          </div>
        </div>
      )}

      {/* Drawer */}
      {openRequest && (
        <BanfDetailDrawer
          request={openRequest}
          canDecide={canDecide}
          canDelete={canDelete}
          processing={processing}
          onClose={() => setOpenId(null)}
          onApprove={askApprove}
          onReject={askReject}
          onReorder={handleReorder}
          onDelete={handleDelete}
          onRetrySap={handleRetrySap}
        />
      )}

      {/* Bestätigungsdialog */}
      <ConfirmDialog
        open={Boolean(confirmDialog)}
        message={confirmDialog?.message || ''}
        confirmLabel={confirmDialog?.confirmLabel || ''}
        cancelLabel={t('common.cancel')}
        variant={confirmDialog?.variant || 'brand'}
        processing={processing}
        inputPlaceholder={confirmDialog?.inputPlaceholder}
        onConfirm={(val) => confirmDialog?.onConfirm?.(val)}
        onCancel={() => setConfirmDialog(null)}
      />
    </div>
  );
};

export default BanfListView;
